import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  AlertCircle, AlertTriangle, CheckCircle, Clock, ExternalLink, Loader2, RefreshCw,
  Music, Eye, Trash2, ListMusic, Mail, FileText, Settings, X, Save, Tag
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  pending_payment: { label: 'Pendiente de pago', color: 'amber', testId: 'status-pending' },
  paid: { label: 'Pagado', color: 'blue', testId: 'status-paid' },
  in_review: { label: 'En revisión', color: 'blue', testId: 'status-review' },
  active: { label: 'Activo en playlists', color: 'green', testId: 'status-active' },
  expired_pending_admin_removal: { label: '⚠️ SACAR de playlists', color: 'red', testId: 'status-remove' },
  expired: { label: 'Expirado · removido', color: 'gray', testId: 'status-expired' },
  cancelled: { label: 'Cancelado', color: 'gray', testId: 'status-cancelled' },
};

function StatusPill({ status }) {
  const info = STATUS_LABELS[status] || { label: status, color: 'gray' };
  const colorMap = {
    amber: 'bg-amber-500/15 text-amber-300 ring-amber-500/30',
    blue: 'bg-blue-500/15 text-blue-300 ring-blue-500/30',
    green: 'bg-green-500/15 text-green-300 ring-green-500/30',
    red: 'bg-red-500/20 text-red-300 ring-red-500/40 animate-pulse',
    gray: 'bg-white/5 text-white/40 ring-white/10',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ring-1 ${colorMap[info.color]}`}>
      {info.label}
    </span>
  );
}

function daysLeft(activeUntil) {
  if (!activeUntil) return null;
  const ms = new Date(activeUntil).getTime() - Date.now();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

const PlaylistPromoAdminPanel = () => {
  const [data, setData] = useState(null);
  const [allPromos, setAllPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [working, setWorking] = useState({});
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesValue, setNotesValue] = useState('');
  const [showPlanEditor, setShowPlanEditor] = useState(false);
  const [planConfig, setPlanConfig] = useState(null);
  const [planSaving, setPlanSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API_URL}/api/playlist-promo/admin/needs-attention`, { withCredentials: true }),
        axios.get(`${API_URL}/api/playlist-promo/admin/all`, { withCredentials: true }),
      ]);
      setData(a.data);
      setAllPromos(b.data || []);
    } catch (e) {
      console.error('Failed to load promos', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openPlanEditor = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/playlist-promo/admin/config`, { withCredentials: true });
      setPlanConfig(r.data);
      setShowPlanEditor(true);
    } catch (e) {
      alert('No se pudo cargar la configuración: ' + (e.response?.data?.detail || e.message));
    }
  };

  const updatePlanField = (planId, field, value) => {
    setPlanConfig((cfg) => ({
      ...cfg,
      plans: { ...cfg.plans, [planId]: { ...cfg.plans[planId], [field]: value } }
    }));
  };

  const savePlan = async (planId) => {
    setPlanSaving(true);
    try {
      const plan = planConfig.plans[planId];
      const payload = {
        name: plan.name, price: plan.price, playlists: plan.playlists,
        duration_days: plan.duration_days, review_hours: plan.review_hours,
        ig_posts: plan.ig_posts || 0, tiktok_posts: plan.tiktok_posts || 0,
        newsletter: !!plan.newsletter, track_of_week: !!plan.track_of_week,
        manager: !!plan.manager, popular: !!plan.popular,
        tagline_es: plan.tagline_es || '',
        features_es: Array.isArray(plan.features_es) ? plan.features_es : (plan.features_es || '').split('\n').filter(Boolean),
        promo_active: !!plan.promo_active,
        promo_label_es: plan.promo_label_es || '',
        promo_discount_pct: plan.promo_discount_pct || 0,
      };
      await axios.put(`${API_URL}/api/playlist-promo/admin/plans/${planId}`, payload, { withCredentials: true });
      alert(`✓ Plan ${plan.name} guardado`);
    } catch (e) {
      alert('Error guardando: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPlanSaving(false);
    }
  };

  const updateStatus = async (id, patch, label = '') => {
    setWorking((w) => ({ ...w, [id]: true }));
    try {
      await axios.patch(`${API_URL}/api/playlist-promo/admin/${id}`, patch, { withCredentials: true });
      await load();
    } catch (e) {
      alert(`Error ${label}: ${e.response?.data?.detail || e.message}`);
    } finally {
      setWorking((w) => ({ ...w, [id]: false }));
    }
  };

  const saveNotes = async (id) => {
    await updateStatus(id, { admin_notes: notesValue }, 'guardando notas');
    setEditingNotes(null);
    setNotesValue('');
  };

  const sendRenewalReminder = async (promo) => {
    // Just opens a mailto draft for now (the scheduler emails automatically when expired)
    const subject = encodeURIComponent('Renueva tu OG Playlist Promotion');
    const body = encodeURIComponent(
      `Hola ${promo.user_name || ''},\n\nTu promoción "${promo.plan_name}" para "${promo.track_title || 'tu track'}" está por expirar o ya expiró.\n\nRenueva aquí: https://www.ogmusicgroup.com/playlist-promotion\n\n— OG MUSIC GROUP`
    );
    window.open(`mailto:${promo.user_email}?subject=${subject}&body=${body}`, '_blank');
  };

  if (loading && !data) {
    return <div className="flex items-center justify-center p-12 text-white/50"><Loader2 className="w-6 h-6 animate-spin" /> Cargando...</div>;
  }

  const counts = data?.counts || {};
  const visiblePromos = filter === 'all'
    ? allPromos
    : allPromos.filter(p => {
        if (filter === 'paid_unactioned') return ['paid', 'in_review'].includes(p.status);
        if (filter === 'expired_pending') return ['expired_pending_admin_removal'].includes(p.status);
        if (filter === 'expired') return p.status === 'expired';
        if (filter === 'active') return p.status === 'active';
        return p.status === filter;
      });

  return (
    <div className="space-y-6" data-testid="playlist-promo-admin-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <ListMusic className="w-6 h-6 text-[#E8720C]" />
            OG Playlist Promotion · Gestión
          </h2>
          <p className="text-sm text-white/50 mt-1">Cliente envía URL de Spotify · Pago confirmado · Tú agregas el track a las playlists</p>
        </div>
        <button onClick={load} className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-sm font-semibold flex items-center gap-2 border border-white/10" data-testid="reload-promos">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Recargar
        </button>
        <button onClick={openPlanEditor} className="ml-2 px-4 py-2 bg-[#E8720C] hover:bg-[#C55A00] rounded-lg text-sm font-semibold flex items-center gap-2 text-white" data-testid="edit-plans-btn">
          <Settings className="w-4 h-4" /> Editar planes y promos
        </button>
      </div>

      {/* Critical Alerts — top of panel for max visibility */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {[
          { key: 'expired_pending', label: 'SACAR de playlists', count: counts.expired_pending, color: 'red', icon: AlertCircle, hint: 'Acción urgente · Cliente NO renovó' },
          { key: 'paid_unactioned', label: 'Agregar a playlists',  count: counts.paid_unactioned, color: 'blue', icon: Clock, hint: 'Pagados pendientes de inclusión' },
          { key: 'expiring_soon',   label: 'Expira en ≤7 días',     count: counts.expiring_soon, color: 'amber', icon: AlertTriangle, hint: 'Avísale al cliente que renueve' },
          { key: 'expired',         label: 'Removidos · sin pago',  count: counts.unpaid_expired, color: 'gray', icon: Eye, hint: 'Histórico · Posible upsell' },
        ].map((c) => {
          const Icon = c.icon;
          const colorMap = {
            red: 'bg-red-500/10 ring-red-500/30 text-red-300',
            amber: 'bg-amber-500/10 ring-amber-500/30 text-amber-300',
            blue: 'bg-blue-500/10 ring-blue-500/30 text-blue-300',
            gray: 'bg-white/5 ring-white/10 text-white/60',
          };
          const isHot = c.color === 'red' && (c.count || 0) > 0;
          return (
            <button
              key={c.key}
              onClick={() => setFilter(c.key)}
              className={`text-left p-4 rounded-xl ring-1 ${colorMap[c.color]} ${isHot ? 'animate-pulse' : ''} ${filter === c.key ? 'ring-2' : ''}`}
              data-testid={`alert-card-${c.key}`}
            >
              <div className="flex items-center justify-between mb-2">
                <Icon className="w-5 h-5" />
                <span className="text-3xl font-bold">{c.count ?? 0}</span>
              </div>
              <p className="text-xs font-bold uppercase tracking-wider">{c.label}</p>
              <p className="text-[10px] opacity-70 mt-1">{c.hint}</p>
            </button>
          );
        })}
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-white/40 mr-2">Filtrar:</span>
        {[
          ['all', `Todos (${allPromos.length})`],
          ['active', 'Activos'],
          ['paid_unactioned', 'Pagados sin agregar'],
          ['expired_pending', '⚠️ Sacar de playlists'],
          ['expired', 'Expirados'],
          ['pending_payment', 'Sin pagar'],
        ].map(([k, lbl]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${filter === k ? 'bg-[#E8720C] text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}
            data-testid={`filter-${k}`}
          >
            {lbl}
          </button>
        ))}
      </div>

      {/* Promos table */}
      <div className="glass-card overflow-hidden">
        {visiblePromos.length === 0 ? (
          <div className="p-12 text-center text-white/40 text-sm">No hay promos en este filtro.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {visiblePromos.map((p) => {
              const dl = daysLeft(p.active_until);
              const isExpired = p.status === 'expired_pending_admin_removal';
              const isPaidWaiting = ['paid', 'in_review'].includes(p.status);
              return (
                <div key={p.id} className={`p-5 hover:bg-white/[0.02] ${isExpired ? 'bg-red-500/5' : ''}`} data-testid={`promo-row-${p.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <StatusPill status={p.status} />
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#E8720C]/15 text-[#E8720C]">{p.plan_name}</span>
                        <span className="text-xs text-white/40">${p.price}</span>
                        {dl !== null && p.status === 'active' && (
                          <span className={`text-[10px] font-bold ${dl <= 3 ? 'text-red-300' : dl <= 7 ? 'text-amber-300' : 'text-white/40'}`}>
                            · {dl > 0 ? `${dl} días restantes` : `vencido hace ${Math.abs(dl)}d`}
                          </span>
                        )}
                      </div>
                      <p className="font-semibold text-white text-sm truncate">
                        {p.track_title || '(sin título)'} {p.artist_name ? <span className="text-white/40">· {p.artist_name}</span> : null}
                      </p>
                      <p className="text-xs text-white/50">{p.user_email} {p.user_name && <span>· {p.user_name}</span>}</p>
                      {p.spotify_url && (
                        <a href={p.spotify_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-green-400 hover:text-green-300 mt-1">
                          <ExternalLink className="w-3 h-3" /> Abrir en Spotify
                        </a>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                      {isPaidWaiting && (
                        <button
                          onClick={() => updateStatus(p.id, { status: 'active' }, 'activando')}
                          disabled={working[p.id]}
                          className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
                          data-testid={`action-activate-${p.id}`}
                        >
                          {working[p.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                          Marcar como agregado a playlists
                        </button>
                      )}
                      {isExpired && (
                        <button
                          onClick={() => updateStatus(p.id, { status: 'expired' }, 'removiendo')}
                          disabled={working[p.id]}
                          className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
                          data-testid={`action-remove-${p.id}`}
                        >
                          {working[p.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                          Confirmar removido de playlists
                        </button>
                      )}
                      {p.status === 'expired' && (
                        <button
                          onClick={() => sendRenewalReminder(p)}
                          className="px-3 py-1.5 bg-[#E8720C]/15 hover:bg-[#E8720C]/25 text-[#E8720C] rounded-lg text-xs font-bold flex items-center gap-1.5 border border-[#E8720C]/30"
                          data-testid={`action-remind-${p.id}`}
                        >
                          <Mail className="w-3 h-3" />
                          Avisar para renovar
                        </button>
                      )}
                      {p.status === 'active' && (
                        <button
                          onClick={() => {
                            if (confirm('¿Marcar como expirado y sacar de playlists?')) {
                              updateStatus(p.id, { status: 'expired_pending_admin_removal' }, 'forzando expiración');
                            }
                          }}
                          disabled={working[p.id]}
                          className="px-3 py-1.5 bg-white/5 hover:bg-amber-500/20 text-amber-400 rounded-lg text-xs font-semibold border border-amber-500/20"
                          data-testid={`action-force-expire-${p.id}`}
                        >
                          Forzar expiración
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Detail row */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-2 text-[11px] text-white/50">
                    <div>
                      <span className="text-white/30">Creado:</span> {p.created_at ? new Date(p.created_at).toLocaleString() : '—'}
                    </div>
                    <div>
                      <span className="text-white/30">Activo hasta:</span> {p.active_until ? new Date(p.active_until).toLocaleString() : '— (no activado)'}
                    </div>
                    <div>
                      <span className="text-white/30">Playlists:</span> {(p.playlists_added || []).length}/{p.playlists_count}
                    </div>
                  </div>

                  {p.notes && (
                    <div className="text-[11px] text-white/40 italic mb-2">📝 Cliente dijo: "{p.notes}"</div>
                  )}

                  {/* Admin notes inline editor */}
                  {editingNotes === p.id ? (
                    <div className="flex gap-2 mt-2">
                      <input
                        autoFocus
                        value={notesValue}
                        onChange={(e) => setNotesValue(e.target.value)}
                        placeholder="Notas internas (qué playlists, observaciones...)"
                        className="flex-1 px-3 py-1.5 bg-white/5 border border-white/10 rounded text-xs"
                        data-testid={`notes-input-${p.id}`}
                      />
                      <button onClick={() => saveNotes(p.id)} className="px-3 py-1.5 bg-[#E8720C] text-white rounded text-xs font-bold">Guardar</button>
                      <button onClick={() => { setEditingNotes(null); setNotesValue(''); }} className="px-3 py-1.5 bg-white/5 text-white/60 rounded text-xs">Cancelar</button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setEditingNotes(p.id); setNotesValue(p.admin_notes || ''); }}
                      className="text-[11px] text-white/40 hover:text-white/70 inline-flex items-center gap-1 mt-1"
                      data-testid={`edit-notes-${p.id}`}
                    >
                      <FileText className="w-3 h-3" />
                      {p.admin_notes ? `Notas: ${p.admin_notes.substring(0, 60)}${p.admin_notes.length > 60 ? '…' : ''}` : 'Agregar nota interna'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Email Templates Preview */}
      <div className="glass-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <Mail className="w-5 h-5 text-[#E8720C]" />
          <h3 className="text-sm font-bold uppercase tracking-wider">Vista previa de emails</h3>
        </div>
        <p className="text-xs text-white/50 mb-4">
          Revisa cómo se ven los correos antes de que lleguen al cliente. Todos usan el branding OG MUSIC con logo oficial.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {[
            { kind: 'paid',           label: '✓ Pago confirmado',     desc: 'Cliente pagó · en cola' },
            { kind: 'activated',      label: '🎶 En playlists',        desc: 'Bienvenida + playlists asignadas' },
            { kind: 'monthly_report', label: '📊 Reporte mensual',     desc: 'Crecimiento + popularidad' },
            { kind: 'expiring_soon',  label: '⏰ Expira en 3 días',    desc: 'Recordatorio para renovar' },
            { kind: 'expired',        label: '🔁 Promo expirada',      desc: 'Renueva para volver' },
            { kind: 'admin_removal',  label: '⚠️ Alerta admin',        desc: 'Sacar de playlists' },
          ].map((t) => (
            <a
              key={t.kind}
              href={`${API_URL}/api/playlist-promo/admin/email-preview/${t.kind}`}
              target="_blank"
              rel="noreferrer"
              className="block p-3 rounded-lg bg-white/[0.02] hover:bg-white/[0.06] border border-white/10 hover:border-[#E8720C]/30 transition-all text-left"
              data-testid={`email-preview-${t.kind}`}
            >
              <p className="text-xs font-bold mb-1">{t.label}</p>
              <p className="text-[10px] text-white/40">{t.desc}</p>
              <p className="text-[10px] text-[#E8720C] mt-1.5 inline-flex items-center gap-1">Ver preview <ExternalLink className="w-2.5 h-2.5" /></p>
            </a>
          ))}
        </div>
      </div>

      {/* Footer info */}
      <div className="text-[11px] text-white/30 text-center">
        💡 El sistema te envía un email automático cuando una promo expira y el cliente no renovó · Última verificación cada hora
      </div>

      {/* Plan Editor Modal */}
      {showPlanEditor && planConfig && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm overflow-y-auto" data-testid="plan-editor-modal">
          <div className="min-h-screen flex items-start justify-center p-4 py-10">
            <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl max-w-5xl w-full p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <Settings className="w-5 h-5 text-[#E8720C]" />
                  Editar planes y promociones
                </h3>
                <button onClick={() => setShowPlanEditor(false)} className="p-2 hover:bg-white/5 rounded-lg" data-testid="close-plan-editor">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {['starter', 'pro', 'elite'].map((pid) => {
                  const p = planConfig.plans[pid] || {};
                  const setF = (k, v) => updatePlanField(pid, k, v);
                  const featuresText = Array.isArray(p.features_es) ? p.features_es.join('\n') : (p.features_es || '');
                  return (
                    <div key={pid} className="bg-white/[0.02] border border-white/10 rounded-xl p-5 space-y-3" data-testid={`plan-editor-${pid}`}>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold text-lg uppercase">{p.name || pid}</h4>
                        {p.promo_active && (
                          <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 text-[10px] font-bold ring-1 ring-red-500/40 flex items-center gap-1">
                            <Tag className="w-3 h-3" /> Promo activa
                          </span>
                        )}
                      </div>

                      <label className="block text-[10px] uppercase font-bold text-white/50">Nombre visible</label>
                      <input type="text" value={p.name || ''} onChange={(e) => setF('name', e.target.value)}
                        className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded text-sm" data-testid={`field-name-${pid}`} />

                      <label className="block text-[10px] uppercase font-bold text-white/50 mt-1">Precio base ($)</label>
                      <input type="number" step="0.01" min="0" value={p.price || 0} onChange={(e) => setF('price', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded text-sm" data-testid={`field-price-${pid}`} />

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] uppercase font-bold text-white/50">Playlists</label>
                          <input type="number" min="1" max="20" value={p.playlists || 0} onChange={(e) => setF('playlists', parseInt(e.target.value) || 0)}
                            className="w-full px-2 py-1.5 bg-black/40 border border-white/10 rounded text-sm" />
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase font-bold text-white/50">Días</label>
                          <input type="number" min="1" max="365" value={p.duration_days || 0} onChange={(e) => setF('duration_days', parseInt(e.target.value) || 0)}
                            className="w-full px-2 py-1.5 bg-black/40 border border-white/10 rounded text-sm" />
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase font-bold text-white/50">Revisión (h)</label>
                          <input type="number" min="1" max="168" value={p.review_hours || 0} onChange={(e) => setF('review_hours', parseInt(e.target.value) || 0)}
                            className="w-full px-2 py-1.5 bg-black/40 border border-white/10 rounded text-sm" />
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase font-bold text-white/50">IG / TT posts</label>
                          <div className="flex gap-1">
                            <input type="number" min="0" max="10" value={p.ig_posts || 0} onChange={(e) => setF('ig_posts', parseInt(e.target.value) || 0)}
                              className="w-1/2 px-2 py-1.5 bg-black/40 border border-white/10 rounded text-sm" placeholder="IG" />
                            <input type="number" min="0" max="10" value={p.tiktok_posts || 0} onChange={(e) => setF('tiktok_posts', parseInt(e.target.value) || 0)}
                              className="w-1/2 px-2 py-1.5 bg-black/40 border border-white/10 rounded text-sm" placeholder="TT" />
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!p.popular} onChange={(e) => setF('popular', e.target.checked)} /> Más popular</label>
                        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!p.newsletter} onChange={(e) => setF('newsletter', e.target.checked)} /> Newsletter</label>
                        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!p.track_of_week} onChange={(e) => setF('track_of_week', e.target.checked)} /> Track of Week</label>
                        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!p.manager} onChange={(e) => setF('manager', e.target.checked)} /> Manager</label>
                      </div>

                      <label className="block text-[10px] uppercase font-bold text-white/50 mt-1">Tagline (subtítulo)</label>
                      <input type="text" value={p.tagline_es || ''} onChange={(e) => setF('tagline_es', e.target.value)}
                        className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded text-sm" />

                      <label className="block text-[10px] uppercase font-bold text-white/50 mt-1">Features (1 por línea)</label>
                      <textarea
                        rows={5}
                        value={featuresText}
                        onChange={(e) => setF('features_es', e.target.value.split('\n'))}
                        className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded text-xs font-mono resize-none"
                        data-testid={`field-features-${pid}`}
                      />

                      <div className="border-t border-white/10 pt-3 mt-3">
                        <label className="flex items-center gap-2 mb-2 cursor-pointer">
                          <input type="checkbox" checked={!!p.promo_active} onChange={(e) => setF('promo_active', e.target.checked)} data-testid={`promo-active-${pid}`} />
                          <span className="text-sm font-bold text-red-300 flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> Activar promoción</span>
                        </label>
                        {p.promo_active && (
                          <div className="space-y-2 ml-6">
                            <div>
                              <label className="block text-[10px] uppercase font-bold text-white/50">Etiqueta visible</label>
                              <input type="text" value={p.promo_label_es || ''} onChange={(e) => setF('promo_label_es', e.target.value)}
                                placeholder="ej: Black Friday -25%"
                                className="w-full px-3 py-2 bg-black/40 border border-red-500/30 rounded text-sm" data-testid={`promo-label-${pid}`} />
                            </div>
                            <div>
                              <label className="block text-[10px] uppercase font-bold text-white/50">% Descuento (0–100)</label>
                              <input type="number" min="0" max="100" value={p.promo_discount_pct || 0} onChange={(e) => setF('promo_discount_pct', parseInt(e.target.value) || 0)}
                                className="w-full px-3 py-2 bg-black/40 border border-red-500/30 rounded text-sm" data-testid={`promo-pct-${pid}`} />
                            </div>
                            <div className="text-xs text-white/50">
                              Precio efectivo: <span className="text-green-400 font-bold">
                                ${(parseFloat(p.price || 0) * (1 - Math.min(100, Math.max(0, parseInt(p.promo_discount_pct || 0))) / 100)).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => savePlan(pid)}
                        disabled={planSaving}
                        className="w-full mt-3 px-4 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] rounded-lg text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 text-white"
                        data-testid={`save-plan-${pid}`}
                      >
                        {planSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Guardar {p.name || pid}
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="text-center mt-6">
                <button onClick={() => setShowPlanEditor(false)} className="px-6 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-sm">Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaylistPromoAdminPanel;
